import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Upload, Loader2, Pencil, Trash2, Image, BookOpen, Eye, EyeOff } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Function to extract first page of PDF as image
const extractFirstPageAsImage = async (file: File): Promise<Blob | null> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    
    const scale = 2; // Higher scale = better quality
    const viewport = page.getViewport({ scale });
    
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    
    const context = canvas.getContext('2d')!;
    await page.render({ canvasContext: context, viewport }).promise;
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
  } catch (error) {
    console.error('Error extracting PDF cover:', error);
    return null;
  }
};

interface Book {
  id: string;
  title: string;
  author: string | null;
  description: string | null;
  summary: string | null;
  cover_url: string | null;
  pdf_url: string;
  page_count: number | null;
  is_published: boolean;
  created_at: string;
}

const LibraryTab = () => {
  const { t } = useTranslation();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const [editForm, setEditForm] = useState({
    title: "",
    author: "",
    description: "",
    summary: "",
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const { data, error } = await supabase
        .from("library_books")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBooks((data as Book[]) || []);
    } catch (error) {
      console.error("Error fetching books:", error);
      toast.error("Erro ao carregar livros");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Por favor, selecione um arquivo PDF");
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 100MB");
      return;
    }

    setUploading(true);
    setProcessing(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Step 1: Extract cover from first page
      toast.info("Extraindo capa do PDF...");
      const coverBlob = await extractFirstPageAsImage(file);

      // Step 2: Upload PDF to storage
      const fileName = `pdfs/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
      const { error: uploadError } = await supabase.storage
        .from("library")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl: pdfUrl } } = supabase.storage
        .from("library")
        .getPublicUrl(fileName);

      // Step 3: Upload cover image to PUBLIC bucket if extracted successfully
      let coverUrl: string | null = null;
      if (coverBlob) {
        const coverFileName = `${Date.now()}-cover.jpg`;
        const { error: coverUploadError } = await supabase.storage
          .from("book-covers")
          .upload(coverFileName, coverBlob, { contentType: 'image/jpeg' });

        if (!coverUploadError) {
          const { data: { publicUrl } } = supabase.storage
            .from("book-covers")
            .getPublicUrl(coverFileName);
          coverUrl = publicUrl;
        } else {
          console.error("Cover upload error:", coverUploadError);
        }
      }

      setProcessing(true);
      toast.info("Processando metadados com IA...");

      // Step 4: Call edge function to process the PDF with AI
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-book-upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ pdfUrl }),
        }
      );

      const aiData = await response.json();
      
      if (!response.ok && !aiData.title) {
        throw new Error(aiData.error || "Erro ao processar PDF");
      }

      // Step 5: Create book record with cover and description
      const { error: insertError } = await supabase
        .from("library_books")
        .insert({
          title: aiData.title || file.name.replace(".pdf", ""),
          author: aiData.author || null,
          description: aiData.description || null,
          summary: aiData.summary || null,
          pdf_url: pdfUrl,
          cover_url: coverUrl,
          is_published: false,
          created_by: user.id,
        });

      if (insertError) throw insertError;

      toast.success(
        coverUrl 
          ? "Livro adicionado com capa extraída automaticamente!" 
          : aiData.aiProcessed 
            ? "Livro adicionado com sucesso! Dados extraídos pela IA." 
            : "Livro adicionado. Edite os dados manualmente."
      );
      
      fetchBooks();

    } catch (error) {
      console.error("Error uploading book:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao fazer upload do livro");
    } finally {
      setUploading(false);
      setProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleEditClick = (book: Book) => {
    setEditingBook(book);
    setEditForm({
      title: book.title,
      author: book.author || "",
      description: book.description || "",
      summary: book.summary || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingBook) return;

    try {
      const { error } = await supabase
        .from("library_books")
        .update({
          title: editForm.title,
          author: editForm.author || null,
          description: editForm.description || null,
          summary: editForm.summary || null,
        })
        .eq("id", editingBook.id);

      if (error) throw error;

      toast.success("Livro atualizado com sucesso");
      setEditDialogOpen(false);
      fetchBooks();
    } catch (error) {
      console.error("Error updating book:", error);
      toast.error("Erro ao atualizar livro");
    }
  };

  const handleCoverUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingBook) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione uma imagem");
      return;
    }

    setCoverUploading(true);

    try {
      // Clean filename and add timestamp
      const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const fileName = `${Date.now()}-${cleanName}`;
      
      console.log("Uploading cover:", fileName, "to book-covers bucket");
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("book-covers")
        .upload(fileName, file, { 
          contentType: file.type,
          upsert: false 
        });

      if (uploadError) {
        console.error("Upload error details:", uploadError);
        throw new Error(`Erro no upload: ${uploadError.message}`);
      }

      console.log("Upload successful:", uploadData);

      const { data: urlData } = supabase.storage
        .from("book-covers")
        .getPublicUrl(fileName);
      
      const coverUrl = urlData.publicUrl;
      console.log("Generated public URL:", coverUrl);

      const { error: updateError } = await supabase
        .from("library_books")
        .update({ cover_url: coverUrl })
        .eq("id", editingBook.id);

      if (updateError) {
        console.error("DB update error:", updateError);
        throw new Error(`Erro ao atualizar banco: ${updateError.message}`);
      }

      toast.success("Capa atualizada com sucesso");
      fetchBooks();
      setEditingBook({ ...editingBook, cover_url: coverUrl });
    } catch (error) {
      console.error("Error uploading cover:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao atualizar capa");
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = "";
      }
    }
  };

  const handleTogglePublish = async (book: Book) => {
    try {
      const { error } = await supabase
        .from("library_books")
        .update({ is_published: !book.is_published })
        .eq("id", book.id);

      if (error) throw error;

      toast.success(book.is_published ? "Livro despublicado" : "Livro publicado");
      fetchBooks();
    } catch (error) {
      console.error("Error toggling publish:", error);
      toast.error("Erro ao alterar publicação");
    }
  };

  const handleDelete = async (book: Book) => {
    try {
      // Delete PDF from storage
      const pdfPath = book.pdf_url.split("/library/")[1];
      if (pdfPath) {
        await supabase.storage.from("library").remove([pdfPath]);
      }

      // Delete cover from storage if exists (check both buckets)
      if (book.cover_url) {
        if (book.cover_url.includes("/book-covers/")) {
          const coverPath = book.cover_url.split("/book-covers/")[1];
          if (coverPath) {
            await supabase.storage.from("book-covers").remove([coverPath]);
          }
        } else if (book.cover_url.includes("/library/")) {
          const coverPath = book.cover_url.split("/library/")[1];
          if (coverPath) {
            await supabase.storage.from("library").remove([coverPath]);
          }
        }
      }

      // Delete book record
      const { error } = await supabase
        .from("library_books")
        .delete()
        .eq("id", book.id);

      if (error) throw error;

      toast.success("Livro excluído com sucesso");
      fetchBooks();
    } catch (error) {
      console.error("Error deleting book:", error);
      toast.error("Erro ao excluir livro");
    }
  };

  const defaultCover = "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=200&h=280&fit=crop";

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Adicionar Novo Livro
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf"
              className="hidden"
              disabled={uploading}
            />
            <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Arraste um PDF ou clique para selecionar
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {processing ? "Processando com IA..." : "Enviando..."}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Selecionar PDF
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Máximo 100MB. A IA extrairá automaticamente título, autor e sumário.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Books List */}
      <Card>
        <CardHeader>
          <CardTitle>Livros na Biblioteca ({books.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : books.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum livro cadastrado ainda.
            </p>
          ) : (
            <div className="grid gap-4">
              {books.map((book) => (
                <div
                  key={book.id}
                  className="flex items-start gap-4 p-4 border rounded-lg bg-card"
                >
                  <img
                    src={book.cover_url || defaultCover}
                    alt={book.title}
                    className="w-16 h-24 object-cover rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = defaultCover;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-semibold text-foreground truncate">
                          {book.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {book.author || "Autor desconhecido"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            book.is_published
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                          }`}
                        >
                          {book.is_published ? "Publicado" : "Rascunho"}
                        </span>
                      </div>
                    </div>
                    {book.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {book.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditClick(book)}
                        className="gap-1"
                      >
                        <Pencil className="w-3 h-3" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTogglePublish(book)}
                        className="gap-1"
                      >
                        {book.is_published ? (
                          <>
                            <EyeOff className="w-3 h-3" />
                            Despublicar
                          </>
                        ) : (
                          <>
                            <Eye className="w-3 h-3" />
                            Publicar
                          </>
                        )}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive" className="gap-1">
                            <Trash2 className="w-3 h-3" />
                            Excluir
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir livro?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser desfeita. O livro "{book.title}" será
                              permanentemente removido da biblioteca.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(book)}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Livro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Cover Preview & Upload */}
            <div className="flex items-start gap-4">
              <div className="relative">
                <img
                  src={editingBook?.cover_url || defaultCover}
                  alt="Capa"
                  className="w-24 h-32 object-cover rounded border"
                  onError={(e) => {
                    console.error("Failed to load cover:", editingBook?.cover_url);
                    (e.target as HTMLImageElement).src = defaultCover;
                  }}
                />
                <input
                  type="file"
                  ref={coverInputRef}
                  onChange={handleCoverUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute -bottom-2 -right-2 h-8 w-8 p-0"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                >
                  {coverUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Image className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="author">Autor</Label>
              <Input
                id="author"
                value={editForm.author}
                onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                placeholder="Nome do autor"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Breve descrição do livro"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="summary">Sumário</Label>
              <Textarea
                id="summary"
                value={editForm.summary}
                onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
                placeholder="Sumário ou índice do livro"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LibraryTab;